import React, { createContext, useCallback, useContext, useEffect, useState } from "react"

import * as Sentry from "@sentry/react"
import { AuthError, Session, User } from "@supabase/supabase-js"
import { JwtPayload, jwtDecode } from "jwt-decode"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { useSessionInvalidation } from "@/hooks/useSessionInvalidation"
import { supabase } from "@/lib/supabase"
import { MixpanelTracking } from "@/services/analytics/mixpanel"
import { getDefaultHomePageForAccessToken } from "@/utils/roleUtils"

// Define custom JWT payload type that extends the standard JwtPayload
interface CustomJwtPayload extends JwtPayload {
  user_role: "Admin" | "Manager" | "DSP" | "Driver" | string
  user_team: string
  role_permissions: string[]
  team_permissions: string[]
}

type AuthContextType = {
  session: Session | null
  user: CustomUser | null
  loading: boolean
  signIn: (
    email: string,
    password: string,
  ) => Promise<{
    error: AuthError | null
    data: { session: Session | null; user: User | null } | null
  }>
  signOut: () => Promise<void>
  isAuthenticated: boolean
  claims: Claims
}

type Claims = {
  user_role: string
  user_team: string
  role_permissions: string[]
  team_permissions: string[]
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type CustomUser = {
  full_name: string
  role: string
  team: string
  sub_teams: string[]
  is_active: boolean
  email: string
  dsp_id?: string
  driver_manager_team_id?: string
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [claims, setClaims] = useState<Claims>({
    user_role: "",
    user_team: "",
    role_permissions: [],
    team_permissions: [],
  })
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<CustomUser | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Check for session invalidations
  const { data: invalidationData } = useSessionInvalidation()

  const fetchUser = async (userId: string) => {
    const { data, error } = await supabase.from("users").select("*").eq("id", userId).single()
    if (error) {
      console.error("Error fetching user:", error)
      return null
    }
    return data
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      if (session?.user) {
        const user = await fetchUser(session.user.id)
        setUser(user)
      } else {
        setUser(null)
      }
      setLoading(false)
    })
    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        const jwt = jwtDecode<CustomJwtPayload>(session.access_token)
        const { user_role, user_team, role_permissions, team_permissions } = jwt
        setClaims({
          user_role,
          user_team,
          role_permissions,
          team_permissions,
        })
        setIsAuthenticated(true)
      }
      setSession(session)

      setLoading(false)
    })
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    async function fetchUserData() {
      if (session?.user) {
        const fetchedUser = await fetchUser(session.user.id)
        setUser(fetchedUser)

        if (fetchedUser) {
          Sentry.setUser({ id: session.user.id, email: fetchedUser.email })
          Sentry.setContext("user_details", {
            fullName: fetchedUser.full_name,
            role: fetchedUser.role,
            team: fetchedUser.team,
          })
          MixpanelTracking.identifyUser({
            id: session.user.id,
            email: fetchedUser.email,
            full_name: fetchedUser.full_name,
          })
        }
      } else {
        setUser(null)
        Sentry.setUser(null)
        Sentry.setContext("user_details", null)
      }
    }

    fetchUserData()
  }, [session])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        toast.error(error.message)
        return { data: null, error }
      }

      if (data?.user && data?.session) {
        toast.success("Logged in successfully")
        navigate(getDefaultHomePageForAccessToken(data.session.access_token))
      }

      return { data, error: null }
    } catch (error) {
      console.error("Error signing in:", error)
      toast.error("An unexpected error occurred")
      return { data: null, error: error as AuthError }
    }
  }

  /**
   * Check if an invalidation has already been processed
   */
  const isInvalidationProcessed = useCallback((invalidationId: string, userId: string): boolean => {
    if (typeof window === "undefined") {
      return false
    }

    try {
      const key = `session-invalidations-processed-${userId}`
      const stored = localStorage.getItem(key)
      return stored === invalidationId
    } catch (error) {
      console.error("Error checking processed invalidation:", error)
      return false
    }
  }, [])

  /**
   * Mark an invalidation as processed in localStorage to prevent repeated logouts
   * Stores only the latest processed invalidation ID
   */
  const markInvalidationAsProcessed = useCallback((invalidationId: string, userId: string) => {
    if (typeof window === "undefined") {
      return
    }

    try {
      const key = `session-invalidations-processed-${userId}`
      // Store only the latest invalidation ID (not an array)
      localStorage.setItem(key, invalidationId)
    } catch (error) {
      console.error("Error marking invalidation as processed:", error)
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut()
      MixpanelTracking.reset()
      toast.success("Logged out successfully")
      navigate("/login")
    } catch (error) {
      console.error("Error signing out:", error)
      toast.error("Error signing out")
    }
  }, [navigate])

  // Auto-logout when session invalidation is detected
  useEffect(() => {
    if (invalidationData?.shouldInvalidate && session?.user && invalidationData.invalidation) {
      const invalidation = invalidationData.invalidation

      // First check if this invalidation has already been processed
      if (isInvalidationProcessed(invalidation.id, session.user.id)) {
        return // Already processed, don't logout again
      }

      const reason = invalidation.reason
      const message = reason
        ? `Your session has been invalidated: ${reason}`
        : "Your session has been invalidated. Please log in again."

      // Mark this invalidation as processed before logging out
      // This prevents repeated logouts for the same invalidation
      markInvalidationAsProcessed(invalidation.id, session.user.id)

      toast.error(message)
      signOut()
    }
  }, [invalidationData, session, signOut, markInvalidationAsProcessed, isInvalidationProcessed])

  const value = {
    session,
    user,
    loading,
    signIn,
    signOut,
    isAuthenticated,
    claims,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
